/**
 * WS-HF — the two-stage case flow, as the wizard's first three screens.
 *
 * This is the owner's Higgsfield canvas, run from the app instead of from the canvas:
 *
 *   Case photos → Stage 1 · Clear case → (human check) → Stage 2 · Print guide → (human check)
 *
 * and then the approved magenta image is handed to the existing mask step exactly as the
 * single-stage path hands over its generated image.
 *
 * Three rules from the canvas's own run control drive every button here:
 *
 *  - **Two AI stages, normally.** Local QC happens between them, and neither starts by itself.
 *  - **One approved retry per stage.** Not a loop. Every attempt is real money, and the
 *    confirmation names the credits before it is spent.
 *  - **A failed, refused or cancelled stage is never shown as a success.** The card says what
 *    happened and offers the retry the operator may approve — it does not advance.
 *
 * And one rule from this app: **a refresh mid-stage resumes by READING the job.** The job id
 * lives in the wizard store, so a navigation away and back re-reads it and polls; it never
 * starts a second generation the operator would pay for twice.
 *
 * **Which presses spend money** (wave 3, fix 2). On the CLOUD path the worker starts every
 * pending stage itself, so the paid presses are *Start stage 1* (create) and *Approve* on stage
 * 1 (which queues stage 2). On the DEVICE path this window runs the stage, so the paid press is
 * *Generate* on a pending stage. Every paid press shows its estimate on the card first and asks
 * through `ConfirmPopover` — never `window.confirm`, which is absent under Tauri and would then
 * answer "yes" for nobody. The executor is the JOB's: a cloud job opened on a desktop that is
 * now connected to an MCP account is still the worker's job.
 *
 * **The company subscription** (WS-HF shared login). The designers are the company's own team,
 * and they all generate on ONE Higgsfield subscription a super-admin connected in Admin. When
 * the capabilities say that wallet is ready, a new job is a CLOUD job whose `creditSource` is
 * `subscription`: the worker generates and this card polls, and the path line reads "Company
 * Higgsfield subscription · <model> · about N credits". A designer never gets the device path,
 * and never sees a Higgsfield connect button: when nothing is connected they read "Image
 * generation is not set up yet. Ask an admin to connect Higgsfield." and there is no Start. A
 * super-admin who chose "This computer only (advanced)" keeps the device path.
 */
import { useGuideSafeUrl } from "../generate/whiteGuide.js";
import { useCallback, useEffect, useRef, useState, type ImgHTMLAttributes, type ReactNode } from "react";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { Icon } from "../ui/Icon.js";
import { InfoHint } from "../ui/Tooltip.js";
import { RemovableImage } from "../ui/RemovableImage.js";
import { useServices } from "../app/context.js";
import { DEFAULT_CASE_RULES, useWizardStore } from "../stores/wizard.js";
import {
  DESIGNER_NOT_SET_UP_TEXT,
  companySubscriptionReady,
  mayGenerateOnThisComputer,
  resolveGenerationSource,
} from "../stores/generationSource.js";
import { useSessionStore } from "../stores/session.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { PRODUCT_HEIGHT_DEFAULT, ProductHeightControl } from "./ProductHeightControl.js";
import { higgsfieldMcp, type HiggsfieldMcpClient } from "@genz/platform";
import {
  CASE_JOB_MODEL_DEFAULTS,
  CASE_WALLET_LABELS,
  listCreditsFor,
  DEFAULT_HF_FRAME,
  frameParams,
  type CaseWallet,
  type HfExecutor,
} from "../generate/higgsfieldModels.js";
import { fetchResultBytes, toDataUri } from "../generate/candidates.js";
import {
  CASE_STAGES,
  MAX_STAGE_ATTEMPTS,
  attemptsOf,
  canApprove,
  canGenerate,
  canRetry,
  creditsOf,
  currentStage,
  failedStage,
  failureText,
  isHeld,
  isRunning,
  phaseOf,
  pollDelay,
  previewUrl,
  requestIdOf,
  retriesLeft,
  type CaseStageKey,
} from "./caseJobState.js";
import {
  caseStageLatchKey,
  isStuckOnUnknownCaseSubmission,
  resumeHeldCaseJobStage,
  runCaseJobStage,
} from "./runCaseJobStage.js";
import { clearLatch, readLatch } from "../generate/deviceSubmissionLatch.js";
import { forgetRun, recallRun, rememberRun } from "../generate/deviceRunRegistry.js";
import { caseRunKey } from "./caseRunKey.js";
import type { AiSettings, CaseJobView, CaseRuleKey, PickedFile } from "@genz/platform";

/** How often the cloud path re-reads a stage the worker owns (queued or running). */

/**
 * An estimate that could not be had. `undefined` is "not asked yet"; `null` is "asked, and no
 * account could quote it" — the web build, a signed-out desktop, or a model the MCP account
 * does not price. The card says "not quoted" for the second, never a number it invented.
 */
type Estimate = number | { upTo: number } | null | undefined;

/** Several stages' estimates added up; a ceiling in any makes the total a ceiling. */
function sumEstimates(list: Estimate[]): Estimate {
  if (list.includes(undefined)) return undefined;
  if (list.includes(null)) return null;
  const total = list.reduce<number>((n, e) => n + (typeof e === "number" ? e : e!.upTo), 0);
  return list.every((e) => typeof e === "number") ? total : { upTo: total };
}

function estimateText(e: Estimate): string {
  if (typeof e === "number") return `about ${e} credits`;
  // No price for the model on screen, but the company's per-stage ceiling is known.
  if (e && typeof e === "object") return `up to ${e.upTo} credits`;
  if (e === null) return "cost not quoted";
  return "quoting the cost…";
}

/** The three photographs, with the caption each one carries on screen. */
const PHOTO_SLOTS: { slot: "phone" | "empty" | "crop"; label: string; caption: string }[] = [
  {
    slot: "phone",
    label: "Phone back",
    caption: "The authority for the phone — colour, logo and the whole camera module.",
  },
  {
    slot: "empty",
    label: "Empty clear case",
    caption: "The actual case, off the phone. The authority for bumpers, rim and openings.",
  },
  {
    slot: "crop",
    label: "Camera-area crop",
    caption: "A close-up of that SAME case's camera window — a detail view, not a second case.",
  },
];

/** The three per-stage rule fields, in the order the stages use them. */
const RULE_FIELDS: { key: CaseRuleKey; label: string }[] = [
  { key: "clear", label: "Stage 1 · Clear case" },
  { key: "magenta", label: "Stage 2 · Print guide" },
  { key: "repair", label: "Repair camera opening" },
];

const IMAGE_ACCEPT = ["image/png", "image/jpeg", "image/webp"];
const MAX_PHOTO_BYTES = 32 * 1024 * 1024;

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** One picked photo, as a thumbnail with a Remove. */
/** A stage preview; only a super-admin sees the print guide's colour in it. */
function GuideSafeImg({ src, ...rest }: ImgHTMLAttributes<HTMLImageElement> & { src: string }) {
  const safe = useGuideSafeUrl(src);
  return safe ? <img {...rest} src={safe} /> : null;
}

function PhotoSlot({
  file, label, caption, onPick, onRemove, testid,
}: {
  file?: PickedFile;
  label: string;
  caption: string;
  onPick: () => void;
  onRemove: () => void;
  testid: string;
}) {
  const [url, setUrl] = useState<string | undefined>();
  useEffect(() => {
    if (!file || typeof URL === "undefined" || !URL.createObjectURL) return;
    const u = URL.createObjectURL(new Blob([file.bytes as BlobPart], { type: file.mime ?? "image/png" }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  return (
    <div className="case-slot" data-testid={`case-slot-${testid}`}>
      <span className="label">{label}</span>
      {file ? (
        <>
          <RemovableImage src={url} alt={label} className="case-slot__img" imgTestId={`case-thumb-${testid}`}
            removeLabel={`Remove ${label.toLowerCase()}`} onRemove={onRemove} removeTestId={`case-remove-${testid}`} />
          <span className="caption case-slot__name">{file.name}</span>
        </>
      ) : (
        <button
          type="button"
          className="btn btn--sm case-slot__add"
          onClick={onPick}
          data-testid={`case-pick-${testid}`}
        >
          <Icon name="upload" size={16} /> Add photo
        </button>
      )}
      {/* A CAPTION, not a hint paragraph under a control (rule 10): it names what the picture
          must BE, which the operator needs before they go and take it. */}
      <span className="caption case-slot__caption">{caption}</span>
    </div>
  );
}

/**
 * Which path a stage will run on, and what it will cost.
 *
 * The operator is about to spend either the platform's money or their own, on a model whose name
 * differs between the two. Saying so on the card is the difference between a considered press
 * and a surprise on the invoice.
 */
function PathLine({
  wallet, model, estimate, testid, estimateTestid,
}: {
  wallet: CaseWallet;
  /** `null`: a designer, and the cloud did not say which model the company configured. */
  model: string | null;
  estimate: Estimate;
  testid: string;
  estimateTestid: string;
}) {
  return (
    <p className="caption case-card__path" data-testid={testid}>
      {CASE_WALLET_LABELS[wallet]} · {model === null ? CONFIGURED_MODEL_TEXT : <code>{model}</code>}
      {" · "}
      <span data-testid={estimateTestid}>{estimateText(estimate)}</span>
      {/* The company subscription's line is the wallet, the model and the cost — its name
          already says whose money it is. */}
      {wallet === "cloud" ? <> · billed to the platform account</> : wallet === "device" ? <> · from your account</> : null}
    </p>
  );
}

/** What a designer reads when the cloud did not say which model the company configured. */
const CONFIGURED_MODEL_TEXT = "the company's configured model";

/** An old device job opened by anyone below super-admin (fix round, item 1). */
const FOREIGN_DEVICE_TEXT = "This job was started on a per-computer sign-in. Start again from the photos.";

/** The sentence a person reads when no company wallet can run a job. A designer cannot fix it,
 *  so they are told who can; a super-admin is told where. */
/** A HELD stage whose outcome is unknown — it may exist and may be billed — as opposed to one
 *  the vendor refused and did not charge (docs/higgsfield.md, "the two meanings of held"). */
function heldOutcomeUnknown(job: CaseJobView, key: CaseStageKey): boolean {
  const code = job.stageErrors?.[key];
  // A customer is sent the code (`GEN-HELD`), an admin the reason (`outcome_unknown`).
  return code === "outcome_unknown" || code === "GEN-HELD" || !code;
}

function notSetUpText(isSuper: boolean): string {
  return isSuper
    ? "Image generation is not set up yet. Connect Higgsfield in Admin → AI providers."
    : DESIGNER_NOT_SET_UP_TEXT;
}

export function CaseFlow({
  /** Injectable for tests; defaults to the process-wide desktop bridge. */
  mcp = higgsfieldMcp(),
  /** Injectable for tests. By default a Higgsfield result comes through the desktop's
   *  `downloadBytes` when there is one, and a cloud-served image through `fetch`. */
  fetchBytes: injectedFetchBytes,
  /** Called with the approved magenta image, to hand it to the mask step. */
  onMagentaApproved,
  /** The wizard's "Advanced: single-stage" escape hatch. */
  onSingleStage,
  /** Which stage is on screen, so the wizard's stepper can point at it. */
  onStageChange,
  /** The real phone every stage prompt names (owner, 2026-09-25) — the wizard's Details
   *  values, which Publish then shows prefilled. Start is off until brand and model exist. */
  product,
  /** The wizard's own product pickers (type, brand, model), shown on the photos step. */
  productFields,
}: {
  mcp?: HiggsfieldMcpClient;
  fetchBytes?: typeof fetchResultBytes;
  onMagentaApproved: (image: {
    dataUri: string; bytes: Uint8Array; mediaType: string; productHeightPct?: number | null; checkNotes?: string[];
  }) => void;
  onSingleStage: () => void;
  product?: { brand: string; model: string; productType?: string };
  productFields?: ReactNode;
  onStageChange?: (key: CaseStageKey | null) => void;
}) {
  const { client, io, logger } = useServices();
  const fetchBytes = injectedFetchBytes ?? fetchResultBytes;
  const fetchVendorBytes = injectedFetchBytes
    ?? ((url: string) => fetchResultBytes(url, io.downloadBytes?.bind(io)));
  const wiz = useWizardStore();
  const isSuper = useSessionStore((st) => st.isSuperAdmin());
  /** Owner rule 2026-09-25: an admin reads the reason; a customer reads a code. */
  const admin = useSessionStore((st) => st.isAdmin());

  const [job, setJob] = useState<CaseJobView | null>(null);
  const [settings, setSettings] = useState<AiSettings | null>(null);
  /** The executor and the AI settings are KNOWN (the settings read may have failed — then the
   *  catalogue defaults are what runs). Nothing is quoted before this: a quote asked on the first
   *  render named models that were never going to run (task 5, WS-HF shared login). */
  const [ready, setReady] = useState(false);
  /** Which executor a NEW job would get — this desktop's current setting. */
  const [deviceExecutor, setDeviceExecutor] = useState<HfExecutor>("cloud");
  /* THE EXECUTOR IS THE JOB'S. Once a job exists it says who runs it, and that does not change
     because the desktop signed in to (or out of) an MCP account in the meantime. */
  const executor: HfExecutor = job ? job.executor : deviceExecutor;
  /** What the capabilities say about the company wallets — `null` until read. Absent fields
   *  (an older cloud) or a failed read gate NOTHING: silence is not an admin's decision. */
  const [shared, setShared] = useState<{
    source?: "subscription" | "api";
    ready?: boolean;
    models?: Partial<Record<CaseStageKey, string>> | null;
    ceiling?: number | null;
    quality?: string;
    height?: number | null;
  } | null>(null);
  /** An admin's per-job product height; `undefined` until touched = the company default. */
  const [height, setHeight] = useState<number | null | undefined>(undefined);
  /* WHOSE MONEY, and so which model pair. A job's is fixed at create (`creditSource`); before
     one exists, a cloud run spends whichever company wallet the capabilities name. */
  const wallet: CaseWallet = executor === "device"
    ? "device"
    : (job ? job.creditSource === "subscription" : shared?.source === "subscription")
      ? "subscription"
      : "cloud";
  /* FIX ROUND, ITEM 1. A device job belongs to a per-computer sign-in, and only a super-admin
     may spend on one. An old device job opened by anyone else is not run, resumed, retried or
     looked up on this machine: it says so, and the next Start makes a new job. */
  const foreignDevice = !!job && job.executor === "device" && !isSuper;
  /** No company wallet can run a new cloud job, so there is nothing to start. */
  const notSetUp = !job && executor === "cloud" && shared?.ready === false;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The last job READ failed (offline, a blip). Cleared by the next read that succeeds. */
  const [readError, setReadError] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  /** Quotes per model id — see `Estimate`. */
  const [estimates, setEstimates] = useState<Record<string, Estimate>>({});
  /** The latch key of the attempt a refused (unknown-outcome) run left behind, for the way out. */
  const [stuckKey, setStuckKey] = useState<string | null>(null);
  const [repair, setRepair] = useState(false);
  /** Which of the photos step's two folds is open — rule 8: one at a time, none by default. */
  const [openFold, setOpenFold] = useState<string | null>(null);
  /** The preview URL whose `<img>` has finished loading. Approve accepts what is ON SCREEN, and
   *  until this equals the card's preview what is on screen is a broken image (fix 5). */
  const [loadedPreview, setLoadedPreview] = useState<string | null>(null);
  /** Guards a double-click from landing a second in-flight generation for one stage. */
  const runningRef = useRef(false);

  // Which executor will run, and which models it will use. Read once; a failure leaves the
  // catalogue defaults, which are correct for a fresh install.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let companyReady = false;
      try {
        const caps = await client.customizationCapabilities?.();
        companyReady = companySubscriptionReady(caps);
        if (!cancelled) {
          setShared({
            source: caps?.shared_generation_source,
            ready: caps?.shared_generation_connected,
            models: caps?.shared_generation_models,
            ceiling: caps?.shared_generation_max_credits_per_stage,
            quality: caps?.higgsfield_quality,
            height: caps?.generation_product_height_pct,
          });
        }
      } catch (e) {
        logger?.log("warn", `Case flow · could not read the capabilities — not gating the Start (${errText(e)})`);
        if (!cancelled) setShared({});
      }
      // The device path is a SUPER-ADMIN's alone, and only while the company subscription is
      // not connected: a designer's machine is never asked.
      const onDevice = mcp.hfMcpAvailable && mayGenerateOnThisComputer() && !companyReady;
      const connected = onDevice ? (await mcp.hfMcpStatus().catch(() => ({ connected: false }))).connected : false;
      const src = resolveGenerationSource(onDevice, connected);
      if (!cancelled) setDeviceExecutor(src === "higgsfield-mcp" ? "device" : "cloud");
      // Only a super-admin can read the AI settings (the route is theirs); everyone else runs
      // the pair the cloud fixes at create, which the catalogue defaults name.
      if (mayGenerateOnThisComputer()) {
        try {
          const s = await client.getAiSettings();
          if (!cancelled) setSettings(s);
        } catch (e) {
          logger?.log("warn", `Case flow · could not read AI settings — using the built-in stage models (${errText(e)})`);
        }
      }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [client, mcp, logger]);

  /* THE MODEL IS THE JOB'S once a job exists: it was fixed at create and is part of what the
     job costs, so the card names what will actually run, not what a settings change since
     would have chosen. Before a job exists, the settings say what the create will fix. */
  const modelFor = useCallback((key: CaseStageKey): string => {
    const fixed = job?.models?.[key];
    if (fixed) return fixed;
    // Anyone below super-admin runs the pair the cloud names (their model fields are ignored).
    const named = !isSuper ? shared?.models?.[key] : undefined;
    if (named) return named;
    const stored = settings?.case_job_models?.[wallet]?.[key];
    return stored || CASE_JOB_MODEL_DEFAULTS[wallet][key];
  }, [job, settings, wallet, isSuper, shared]);
  /* Fix round, item 2: the model a person is TOLD about. A designer on a company wallet whose
     cloud did not name the pair is told "the company's configured model" — never the built-in
     default, which the company may not have configured. */
  /** The model that will run, when it is known here (null: the company's, unnamed). */
  const knownModel = (key: CaseStageKey): string | null =>
    !job?.models?.[key] && !isSuper && executor === "cloud" && !shared?.models?.[key]
      ? null
      : modelFor(key);
  // A vendor model id is technical text: a customer never reads one (owner decision, R9).
  const shownModel = (key: CaseStageKey): string | null => (admin ? knownModel(key) : null);

  /** Params a model is sent. Only values the model actually accepts. The canvas runs 1k and
   *  the local normalisation is 1024²; anything larger is paid for and then thrown away. */
  const quality = shared?.quality ?? DEFAULT_HF_FRAME.quality;
  const paramsForModel = useCallback(
    (model: string) => frameParams(model, { resolution: "1k", quality }),
    [quality],
  );
  const paramsFor = useCallback((key: CaseStageKey) => paramsForModel(modelFor(key)), [modelFor, paramsForModel]);

  /**
   * THE ESTIMATE, BEFORE THE PRESS. `get_cost` submits nothing, so every model the next paid
   * press could run is quoted as soon as it is known and shown on the card. Only an MCP
   * account can quote; without one the card says "not quoted" rather than a number nobody gave.
   */
  const estimateModels = [modelFor("clear"), modelFor("magenta")];
  /* Only a super-admin's desktop quotes over MCP. The company subscription's price is the
     published one (`listCreditsFor`): the worker quotes on the company account itself. */
  const canQuote = mcp.hfMcpAvailable && isSuper;
  useEffect(() => {
    if (!ready || wallet === "subscription") return;
    let cancelled = false;
    for (const model of estimateModels) {
      if (model in estimates) continue;
      if (!canQuote) {
        setEstimates((m) => ({ ...m, [model]: null }));
        continue;
      }
      setEstimates((m) => ({ ...m, [model]: undefined }));
      void mcp.hfMcpEstimate(model, paramsForModel(model))
        .then(({ credits }) => { if (!cancelled) setEstimates((m) => ({ ...m, [model]: credits })); })
        .catch((e: unknown) => {
          logger?.log("warn", `Case flow · Higgsfield cost estimate failed for ${model} — ${errText(e)}`);
          if (!cancelled) setEstimates((m) => ({ ...m, [model]: null }));
        });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, wallet, estimateModels.join("|"), canQuote]);
  const estimateFor = (key: CaseStageKey): Estimate => {
    if (wallet !== "subscription") return estimates[modelFor(key)];
    const listed = knownModel(key) === null ? undefined : listCreditsFor(modelFor(key), paramsFor(key));
    if (listed !== undefined) return listed;
    // No published price for what will run: the company's ceiling is the honest bound.
    return typeof shared?.ceiling === "number" ? { upTo: shared.ceiling } : null;
  };

  /**
   * THE RESUME PATH.
   *
   * Runs on mount whenever the store already holds a job id. It READS the job and shows
   * whatever it finds — including a stage still running, which it then polls. It never
   * generates: the first generation is still in flight and the operator would pay for both.
   */
  const refresh = useCallback(async (id: string) => {
    try {
      setJob(await client.caseJobs.get(id));
      setReadError(null);
    } catch (e) {
      setReadError(errText(e));
    }
  }, [client]);

  useEffect(() => {
    if (wiz.caseJobId && !job) void refresh(wiz.caseJobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wiz.caseJobId]);

  // The stepper above this card points at the stage on screen, so "where am I" is answered in
  // the same place for this flow as for every other step.
  useEffect(() => {
    onStageChange?.(job ? currentStage(job.stage) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.stage]);

  /* A finished or failed job is forgotten under its photos' hash: pressing Start with the same
     photos again is then a deliberate new run, not a replay of this one. */
  useEffect(() => {
    if (!job || (job.stage !== "done" && job.stage !== "failed")) return;
    if (!wiz.casePhone || !wiz.caseEmpty || !wiz.caseCrop) return;
    void caseRunKey(wiz.casePhone.bytes, wiz.caseEmpty.bytes, wiz.caseCrop.bytes).then(forgetRun);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.stage]);

  /* A foreign device job is forgotten under its photos' hash, so the next Start is a new job
     rather than a resume of one this person cannot finish. */
  useEffect(() => {
    if (!foreignDevice || !wiz.casePhone || !wiz.caseEmpty || !wiz.caseCrop) return;
    void caseRunKey(wiz.casePhone.bytes, wiz.caseEmpty.bytes, wiz.caseCrop.bytes).then(forgetRun);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foreignDevice, job?.id]);

  /* The cloud path's poll: while the WORKER owns the stage — queued as well as running. On the
     device path this window is the thing doing the generating, and `runCaseJobStage` returns
     the updated job itself. */
  useEffect(() => {
    const delay = job ? pollDelay(job) : null;
    if (!job || delay === null) return;
    const t = setInterval(() => { void refresh(job.id); }, delay);
    return () => clearInterval(t);
  }, [job, refresh]);

  /* THE DEVICE RESUME. A remount or a reload that finds a pending device stage whose attempt
     is latched `submitted` picks the wait back up on the vendor job it already started. It
     never submits: `runCaseJobStage` resumes on the recorded ids. Anything else waits for the
     operator's press. */
  useEffect(() => {
    if (!job || job.executor !== "device" || runningRef.current) return;
    if (!mayGenerateOnThisComputer()) return;
    const key = currentStage(job.stage);
    if (!key || phaseOf(job.stage) !== "pending") return;
    const latched = readLatch(caseStageLatchKey(job.id, key, attemptsOf(job, key)));
    if (latched?.state === "submitted" && latched.jobIds?.length) void runDeviceStage(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.stage]);

  const pick = (slot: "phone" | "empty" | "crop") => async () => {
    setError(null);
    try {
      const files = await io.pickFiles({ accept: IMAGE_ACCEPT, multiple: false, maxBytes: MAX_PHOTO_BYTES });
      if (files.length === 0) return;
      wiz.setCasePhoto(slot, files[0]);
    } catch (e) {
      setError(errText(e));
    }
  };

  /* An EMPTY rule is allowed: the server fills it with the canvas sentence for this case
     (`CASE_RULE_DEFAULTS`), which is exactly what the prefilled text was. */
  const productNamed = !!product?.brand.trim() && !!product?.model.trim();
  const shownHeight = height !== undefined ? height
    : shared?.height === undefined ? PRODUCT_HEIGHT_DEFAULT : shared.height;
  const photosReady = !!wiz.casePhone && !!wiz.caseEmpty && !!wiz.caseCrop && productNamed;

  /**
   * Create the job.
   *
   * On the CLOUD path this is the paid press for stage 1: the worker starts a pending stage
   * itself, so the credits are committed the moment the job exists — which is why the button
   * asks first. On the device path it spends nothing; the paid press is Generate on the card.
   */
  async function startJob() {
    if (!photosReady || busy) return;
    setBusy(true);
    setError(null);
    try {
      /* THE RELOAD RESUME. The store is memory, so after a reload the operator picks the same
         photos and presses Start again. The job those photos already created is remembered
         under their hash (`deviceRunRegistry`), and a job that is still going is RESUMED here
         rather than created — and paid for — a second time. A finished or failed one is
         forgotten: the next Start is a deliberate new run. */
      const key = await caseRunKey(wiz.casePhone!.bytes, wiz.caseEmpty!.bytes, wiz.caseCrop!.bytes);
      const remembered = recallRun(key);
      if (remembered) {
        let existing: CaseJobView | null = null;
        try {
          existing = await client.caseJobs.get(remembered);
        } catch (e) {
          logger?.log("warn", `Case flow · the remembered job ${remembered} could not be read — starting a new one (${errText(e)})`);
        }
        // An old device job is resumed only by a super-admin; anyone else starts a new job.
        const resumable = existing && existing.stage !== "done" && existing.stage !== "failed"
          && (existing.executor !== "device" || mayGenerateOnThisComputer());
        if (existing && resumable) {
          wiz.setCaseJobId(existing.id);
          setJob(existing);
          wiz.setStep("stages");
          return;
        }
        forgetRun(key);
      }
      const created = await client.caseJobs.create({
        phone: wiz.casePhone!.bytes,
        case: wiz.caseEmpty!.bytes,
        crop: wiz.caseCrop!.bytes,
        // A field still at its default goes EMPTY: the server renders its own current
        // sentence, so a default corrected there reaches every job without a client release.
        caseRules: Object.fromEntries(
          RULE_FIELDS.map((f) => [f.key, wiz.caseRules[f.key] === DEFAULT_CASE_RULES[f.key] ? "" : wiz.caseRules[f.key]]),
        ) as Record<CaseRuleKey, string>,
        models: { clear: modelFor("clear"), magenta: modelFor("magenta") },
        executor: executor === "device" ? "device" : "cloud",
        brand: product!.brand,
        model: product!.model,
        productType: product!.productType,
        ...(admin ? { productHeightPct: shownHeight } : {}),
        mime: wiz.casePhone!.mime,
      });
      // Recorded BEFORE anything else can fail, so a reload finds the job rather than making
      // a second one — in the store for this session, under the photos' hash for the next.
      wiz.setCaseJobId(created.id);
      rememberRun(key, created.id);
      setJob(created);
      wiz.setStep("stages");
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * THE DEVICE PATH'S PAID PRESS: run the pending stage on this machine.
   *
   * `runCaseJobStage` reads the job, quotes, latches the attempt, submits, waits and posts the
   * result. The attempt number keys the latch, so a retry is never mistaken for a resume of the
   * first run and a remount mid-wait resumes rather than resubmits.
   */
  async function runDeviceStage(key: CaseStageKey, current: CaseJobView | null = job) {
    if (!current || runningRef.current) return;
    // The device path spends on a per-computer sign-in: a super-admin's alone.
    if (!mayGenerateOnThisComputer()) return;
    const job = current;
    runningRef.current = true;
    setBusy(true);
    setError(null);
    setStuckKey(null);
    setPhase(null);
    const attempt = attemptsOf(job, key);
    // The photos→job mapping, when the photos are still in the store (they are not after a
    // reload): a refused report that shows the stage moved on forgets it.
    const runKey = wiz.casePhone && wiz.caseEmpty && wiz.caseCrop
      ? await caseRunKey(wiz.casePhone.bytes, wiz.caseEmpty.bytes, wiz.caseCrop.bytes)
      : undefined;
    try {
      setJob(await runCaseJobStage({
        client,
        jobId: job.id,
        stage: key,
        attempt,
        runKey,
        model: modelFor(key),
        params: paramsFor(key),
        mcp,
        fetchBytes: fetchVendorBytes,
        logger,
        onPhase: setPhase,
        onEstimate: (credits) => setEstimates((m) => ({ ...m, [modelFor(key)]: credits })),
      }));
    } catch (e) {
      setError(errText(e));
      if (isStuckOnUnknownCaseSubmission(e)) setStuckKey(caseStageLatchKey(job.id, key, attempt));
      // Whatever happened, the JOB is the truth about what was spent — re-read it rather than
      // leaving the card showing a state the server does not agree with.
      await refresh(job.id);
    } finally {
      runningRef.current = false;
      setBusy(false);
      setPhase(null);
    }
  }

  /**
   * "Check again" on a held stage. Cloud: re-read (the worker looks the request up itself).
   * Device: re-read, then ask the vendor once for the held request and post its late image —
   * `resumeHeldCaseJobStage` — so a lost upload is recovered without paying again.
   */
  async function checkAgain(key: CaseStageKey) {
    if (!job || runningRef.current) return;
    runningRef.current = true;
    setBusy(true);
    setError(null);
    try {
      // Only a super-admin's machine asks the vendor for a held device request; for anyone
      // else "Check again" is a plain re-read (the cloud worker looks cloud requests up).
      setJob(job.executor === "device" && !mayGenerateOnThisComputer()
        ? await client.caseJobs.get(job.id)
        : await resumeHeldCaseJobStage({ client, jobId: job.id, stage: key, mcp, fetchBytes: fetchVendorBytes }));
    } catch (e) {
      setError(errText(e));
      await refresh(job.id);
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }

  /**
   * Buy the one retry. `retry` is the server's transition: it counts the attempt and moves the
   * stage back to pending. On the cloud path the worker then runs it and the poll follows; on
   * the device path this window runs it.
   */
  async function retryStage(key: CaseStageKey) {
    if (!job || runningRef.current) return;
    setBusy(true);
    setError(null);
    let next: CaseJobView;
    try {
      next = await client.caseJobs.retry(job.id, { repair });
      setRepair(false);
      setJob(next);
    } catch (e) {
      setError(errText(e));
      await refresh(job.id);
      setBusy(false);
      return;
    }
    setBusy(false);
    if (next.executor === "device" && canGenerate(next, key) && mayGenerateOnThisComputer()) {
      // Run on the job as the retry left it — the attempt count just moved.
      await runDeviceStage(key, next);
    }
  }

  /**
   * Accept the stage under review. Stage 1 asks the server to open stage 2.
   *
   * Stage 2 is a HAND-OVER and nothing else (WS-AUTOKEY, owner decisions 1 + 4): the mask step
   * keys the magenta by itself, and when it finds none the person must still be able to retry
   * stage 2. The server refuses `retry` on a `done` job, so the job stays under review here and
   * the wizard finishes it when the template is published.
   */
  async function approve(key: CaseStageKey) {
    if (!job || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (key === "magenta") {
        await handOver(job);
        return;
      }
      setJob(await client.caseJobs.approve(job.id));
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Hand the magenta image under review to the mask step.
   *
   * A stage 2 with no `magentaResultUrl` is NOT a success: it has nothing to give. Refusing here
   * is what keeps an empty result out of a print file.
   */
  async function handOver(next: CaseJobView) {
    if (!canApprove(next, "magenta") || !next.magentaResultUrl) {
      setError("Stage 2 has no print guide image, so there is nothing to mask. Retry stage 2.");
      return;
    }
    const { bytes, mediaType } = await fetchBytes(next.magentaResultUrl);
    onMagentaApproved({
      dataUri: toDataUri(bytes, mediaType), bytes, mediaType, checkNotes: next.checkNotes?.magenta,
      productHeightPct: next.productHeightPct,
    });
  }

  /* THE QUESTIONS. One sentence each, naming the path, the model and the estimate — the three
     things the operator is about to spend on. Rendered by `ConfirmPopover`, which fails CLOSED:
     no popover, no run. */
  function pathWords(key: CaseStageKey): string {
    return `${CASE_WALLET_LABELS[wallet]} (${shownModel(key) ?? CONFIGURED_MODEL_TEXT}) · ` +
      estimateText(estimateFor(key));
  }
  function startQuestion(): string {
    return `Start Stage 1 · Clear case on ${pathWords("clear")}? Stage 1 runs as soon as the job exists.`;
  }
  function generateQuestion(key: CaseStageKey): string {
    const label = CASE_STAGES.find((s) => s.key === key)!.label;
    return `Run ${label} on ${pathWords(key)}?`;
  }
  function approveQuestion(): string {
    return `Approve stage 1 and queue Stage 2 · Print guide on ${pathWords("magenta")}?`;
  }
  function retryQuestion(key: CaseStageKey): string {
    const spent = creditsOf(job!, key);
    const stages = job!.retryStages?.length ? job!.retryStages : [key];
    const label = stages.map((k) => CASE_STAGES.find((s) => s.key === k)!.label).join(" and ");
    const path = stages.length === 1 ? pathWords(key)
      : `${CASE_WALLET_LABELS[wallet]} · ${estimateText(sumEstimates(stages.map(estimateFor)))}`;
    /* A HELD stage's question says which kind of held it is (docs/higgsfield.md, "the two
       meanings of held"): outcome unknown → the request may already be billed, so check it
       first; a vendor refusal → not charged, so this is simply a fresh attempt. */
    let heldNote = "";
    if (isHeld(job!)) {
      // The request lives on WHOSE account: the company's on the subscription wallet.
      const where = wallet === "subscription" ? "on the company Higgsfield account" : "in your Higgsfield history";
      heldNote = heldOutcomeUnknown(job!, key)
        ? ` Check request ${requestIdOf(job!, key) ?? "(not recorded)"} ${where} first — a retry is a second paid generation, and that image may already be billed.`
        : " The previous attempt was refused by Higgsfield and not charged; this is a fresh attempt.";
    }
    return (
      `Run ${label} again on ${path}?` +
      `${spent ? ` ${spent} credits have been spent on this stage so far.` : ""}` +
      heldNote +
      " This is the only retry this stage has."
    );
  }

  // ── Step 1 · Case photos ──────────────────────────────────────────────────
  if (wiz.step === "case") {
    return (
      <div className="wizard__step-card" data-testid="case-photos-step">
        {error && <p className="banner banner--danger" role="alert" data-testid="case-error"><ErrorNotice detail={error} /></p>}

        <div className="case-grid">
          {PHOTO_SLOTS.map((p) => (
            <PhotoSlot
              key={p.slot}
              file={p.slot === "phone" ? wiz.casePhone : p.slot === "empty" ? wiz.caseEmpty : wiz.caseCrop}
              label={p.label}
              caption={p.caption}
              onPick={pick(p.slot)}
              onRemove={() => wiz.setCasePhoto(p.slot, undefined)}
              testid={p.slot}
            />
          ))}
        </div>

        {/* ONE fold, THREE texts. Each stage says something different about the same window —
            stage 1 draws it open, stage 2 leaves it unprinted, the repair removes what a render
            invented over it — so one shared paragraph would put a stage-1 sentence into the
            prompt that has to say the opposite. Closed by default because the defaults are right
            for the case on the canvas; the fold's meta says when one has been edited.

            Both folds on this step are items of ONE `ui/Accordion` (rule 8): the primitive the
            measured gate opens and measures (G-26o), where a hand-rolled toggle was a fold the
            gate could not see into. */}
        <Accordion
          name="case-photos"
          openKey={openFold}
          onOpenChange={setOpenFold}
          items={[
            {
              key: "rules",
              head: <span data-testid="case-rules-toggle">Case rules (per stage)</span>,
              meta: RULE_FIELDS.some((f) => wiz.caseRules[f.key] !== DEFAULT_CASE_RULES[f.key])
                ? <span className="badge badge--neutral" data-testid="case-rules-edited">edited</span>
                : null,
              body: (
                <div className="case-rules__panel" data-testid="case-rules-panel">
                  {RULE_FIELDS.map((f) => (
                    <div className="field" key={f.key}>
                      <div className="case-rules__head">
                        <label htmlFor={`case-rules-${f.key}`}>
                          {f.label}{" "}
                          <InfoHint text="This stage's one camera-opening sentence, pasted into the prompt's rule slot. Replace it when the case reference changes; leave it empty to use the canvas default." />
                        </label>
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          disabled={wiz.caseRules[f.key] === DEFAULT_CASE_RULES[f.key]}
                          onClick={() => wiz.setCaseRule(f.key, DEFAULT_CASE_RULES[f.key])}
                          data-testid={`case-rules-reset-${f.key}`}
                        >
                          Reset to default
                        </button>
                      </div>
                      <textarea
                        id={`case-rules-${f.key}`}
                        className="input case-rules__text"
                        rows={3}
                        value={wiz.caseRules[f.key]}
                        onChange={(e) => wiz.setCaseRule(f.key, e.target.value)}
                        data-testid={`case-rules-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
              ),
            },
            {
              /* An escape hatch belongs one press away, not competing with the flow the operator
                 is meant to use. */
              key: "advanced",
              head: <span data-testid="case-advanced-toggle">Advanced: single-stage</span>,
              body: (
                <p className="case-advanced">
                  <span className="caption">The older single-prompt generate: reference photos, product images and a free-text instruction.</span>
                  <button type="button" className="btn" onClick={onSingleStage} data-testid="case-single-stage">
                    Use the single-prompt generate
                  </button>
                </p>
              ),
            },
          ]}
        />

        {/* READ-ONLY. The models are an admin setting; showing them here is so the operator
            knows what is about to run and what it will be billed on, not so they can change it
            per run — a per-run model would make two runs of one product incomparable. */}
        <div className="table-scroll">
          <table className="table" data-testid="case-stage-models">
            <thead><tr><th>Stage</th><th>Runs on</th><th>Model</th><th>Cost</th></tr></thead>
            <tbody>
              {CASE_STAGES.map((s) => (
                <tr key={s.key}>
                  <td>{s.label}</td>
                  <td>{CASE_WALLET_LABELS[wallet]}</td>
                  <td>
                    {shownModel(s.key) === null
                      ? <span data-testid={`case-model-${s.key}`}>{CONFIGURED_MODEL_TEXT}</span>
                      : <code data-testid={`case-model-${s.key}`}>{modelFor(s.key)}</code>}
                  </td>
                  <td data-testid={`case-estimate-${s.key}`}>{estimateText(estimateFor(s.key))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {productFields}
        {admin && <ProductHeightControl value={shownHeight} onChange={setHeight} testId="case-height" />}
        {!productNamed && (
          <p className="caption" data-testid="case-product-missing">Pick the brand and model to start.</p>
        )}

        {notSetUp ? (
          /* No Start — and no empty sticky bar where it would be: the server would refuse the
             press (503), and a button that cannot work lies. The sentence says who can fix it. */
          <p className="banner banner--warn" role="status" data-testid="case-not-set-up">{notSetUpText(isSuper)}</p>
        ) : (
        <ActionBar
          label="Case photos actions"
          primary={
            executor === "cloud" ? (
              /* THE PAID PRESS on the cloud path: the worker starts stage 1 the moment the job
                 exists, so the question is asked here and not on the next card. */
              <ConfirmPopover message={startQuestion()} confirmLabel="Start stage 1" onConfirm={() => void startJob()}>
                {(toggle) => (
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!photosReady || busy || !ready}
                    onClick={toggle}
                    data-testid="case-start"
                  >
                    {busy ? "Starting…" : "Start stage 1"}
                  </button>
                )}
              </ConfirmPopover>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                disabled={!photosReady || busy || !ready}
                onClick={() => void startJob()}
                data-testid="case-start"
              >
                {busy ? "Starting…" : "Start stage 1"}
              </button>
            )
          }
        />
        )}
      </div>
    );
  }

  // ── Steps 2 and 3 · the stage cards ───────────────────────────────────────
  if (!job) {
    return (
      <div className="wizard__step-card" data-testid="case-stage-loading">
        {readError ? (
          <div className="banner banner--danger" role="alert">
            <ErrorNotice detail={readError} reference={wiz.caseJobId} />
            <button type="button" className="btn btn--sm" data-testid="case-check-again"
              onClick={() => wiz.caseJobId && void refresh(wiz.caseJobId)}>Check again</button>
          </div>
        ) : <p className="faint">Reading the case job…</p>}
      </div>
    );
  }

  const key = currentStage(job.stage)
    ?? (job.stage === "done" ? "magenta" : job.stage === "failed" ? failedStage(job) : "clear");
  const meta = CASE_STAGES.find((s) => s.key === key)!;
  const ph = phaseOf(job.stage);
  const preview = previewUrl(job, key);
  const previewLoaded = !!preview && loadedPreview === preview;
  const failure = failureText(job);
  /* Fix round, item 3. On the company subscription a held request is on the COMPANY's account,
     which only an admin can open. A designer may not buy a second attempt at a request that
     may already be billed: they ask an admin. A refused (not charged) hold is a fresh attempt. */
  const askAdmin = isHeld(job) && heldOutcomeUnknown(job, key)
    && (!admin || (wallet === "subscription" && !isSuper));
  const mayRetry = canRetry(job, key) && !askAdmin && job.retryStages?.length !== 0;
  const startAgain = () => { wiz.setCaseJobId(undefined); setJob(null); setLoadedPreview(null); wiz.setStep("case"); };

  if (foreignDevice) {
    /* Nothing on this card can spend or accept: the job's stages run on a per-computer
       sign-in this person may not use. The one way on is a new job from the photos. */
    return (
      <div className="wizard__step-card case-card" data-testid={`case-stage-${key}`}>
        <div className="panel-head">
          <div>
            <strong className="panel-title">{meta.label}</strong>
          </div>
        </div>
        <p className="banner banner--warn" role="status" data-testid="case-foreign-device">{FOREIGN_DEVICE_TEXT}</p>
        <ActionBar
          label={`${meta.label} actions`}
          primary={
            <button type="button" className="btn btn--primary" onClick={startAgain} data-testid="case-start-again">
              Start again from the photos
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="wizard__step-card case-card" data-testid={`case-stage-${key}`}>
      <div className="panel-head">
        <div>
          <strong className="panel-title">{meta.label}</strong>
          <p className="panel-sub">{meta.hint}</p>
        </div>
      </div>

      <PathLine
        wallet={wallet}
        model={shownModel(key)}
        estimate={estimateFor(key)}
        testid={`case-path-${key}`}
        estimateTestid={`case-estimate-${key}`}
      />

      {(error ?? readError) && <p className="banner banner--danger" role="alert" data-testid="case-error">
        <ErrorNotice detail={error ?? readError} reference={job.id} /></p>}
      {/* A failed, refused or cancelled stage NEVER reads as a success. It says what happened
          and waits for the operator; it does not advance and it shows no preview as a result. */}
      {failure && <p className="banner banner--danger" role="alert" data-testid="case-failure">
        <ErrorNotice detail={failure} reference={job.id} /></p>}
      {/* A3: the automatic checks are advisory — a quiet note beside the image, never a stop. */}
      {(job.checkNotes?.[key]?.length ?? 0) > 0 && (
        <p className="banner banner--warn" role="status" data-testid="case-check-notes">
          {job.checkNotes![key]!.map((n, i) => <ErrorNotice key={i} detail={n} reference={job.id} advisory />)}
        </p>
      )}

      {/* HELD — submitted, outcome unknown. Not the danger tone: nothing is known to have gone
          wrong, and the image may exist. Not a result either: there is nothing to show. So it is
          a warn-toned STATUS with the request id to check by, and the one action that cannot pay
          twice — read the job again. Generate, Retry and Approve are all withheld. */}
      {isHeld(job) && (
        <div className="banner banner--warn case-held" role="status" data-testid="case-held">
          {!admin ? <ErrorNotice detail={job.stageErrors?.[key] ?? "outcome_unknown"} reference={job.id} /> : <span>
            Submitted to Higgsfield, outcome unknown — request id{" "}
            <code data-testid="case-held-request">{requestIdOf(job, key) ?? "(not recorded)"}</code>.{" "}
            {/* The SERVER's sentence says which kind of held this is: may be billed, or refused
                and not charged. Its words, not a guess made here. */}
            {job.heldReasons?.[key]
              ?? "It may still finish, and it may already have been billed. Check it before running the stage again."}
          </span>}
        </div>
      )}

      {askAdmin && (
        <p className="caption" data-testid="case-held-ask-admin">
          {admin
            ? `Ask an admin to check request ${requestIdOf(job, key) ?? "(not recorded)"} on the company account first.`
            : <ErrorNotice detail={job.stageErrors?.[key] ?? "outcome_unknown"} reference={job.id} />}
        </p>
      )}

      {stuckKey && (
        /* THE WAY OUT of an unknown-outcome attempt (wave 2's N6, for a stage). The operator has
           looked at their Higgsfield account; only they can say it is safe to spend again. */
        <p className="banner banner--warn" role="status" data-testid="case-stuck">
          <span>Check your Higgsfield account for this stage before running it again.</span>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => { clearLatch(stuckKey); setStuckKey(null); setError(null); }}
            data-testid="case-clear-latch"
          >
            I have checked — allow a new run
          </button>
        </p>
      )}

      {/* The bar follows the WORKER's stage on the cloud path (queued, running) and THIS RUN's
          phase on the device path, where the job stays pending while this window generates. */}
      {(isRunning(job) || !!phase || (ph === "pending" && executor === "cloud")) && (
        <div className="gen-progress" data-testid="case-progress" role="status" aria-live="polite">
          <div className="gen-progress__head">
            <span>
              {phase
                ? `Higgsfield · ${phase}…`
                : ph === "pending" ? "Queued — the platform worker starts it…" : "Generating…"}
            </span>
          </div>
          <div className="gen-progress__track"><div className="gen-progress__bar" /></div>
        </div>
      )}

      {ph === "review" && preview && (
        <div className="wizard__generated">
          <div className="wizard__generated-head">
            <span className="label">Result</span>
            <span className="caption" data-testid="case-attempts">
              Attempt {job.attempts[key] || 1} of {MAX_STAGE_ATTEMPTS}
              {creditsOf(job, key) ? ` · ${creditsOf(job, key)} credits` : ""}
            </span>
          </div>
          <GuideSafeImg
            className="wizard__generated-img"
            src={preview}
            alt={`${meta.label} result`}
            onLoad={() => setLoadedPreview(preview)}
            onError={() => {
              setLoadedPreview(null);
              setError("The preview could not be loaded, so there is nothing to approve yet. Check again to re-read the job.");
            }}
            data-testid={`case-preview-${key}`}
          />
          {previewLoaded ? null : (
            <span className="caption" role="status" data-testid="case-preview-pending">Loading the preview…</span>
          )}
        </div>
      )}

      {mayRetry && (
        <div className="field field--check">
          <label htmlFor="case-repair">
            <input
              id="case-repair"
              type="checkbox"
              checked={repair}
              onChange={(e) => setRepair(e.target.checked)}
              data-testid="case-repair"
            />
            {" "}Repair camera opening
            <InfoHint text="Runs the archived correction prompt instead of a fresh render: it returns this packshot unchanged except for the camera opening." />
          </label>
        </div>
      )}

      {(ph === "review" || ph === "held") && !canRetry(job, key) && !askAdmin && (
        /* A disabled Retry with no sentence beside it is indistinguishable from a broken one. */
        <p className="caption" data-testid="case-retry-used">
          This stage&rsquo;s one retry is used.
          {ph === "review" ? " Approve it, or start again from the photos." : " Check the request, or start again from the photos."}
        </p>
      )}

      <ActionBar
        label={`${meta.label} actions`}
        primary={
          canGenerate(job, key) ? (
            /* THE PAID PRESS on the device path. */
            <ConfirmPopover message={generateQuestion(key)} confirmLabel="Generate" onConfirm={() => void runDeviceStage(key)}>
              {(toggle) => (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={busy}
                  onClick={toggle}
                  data-testid="case-generate"
                >
                  {busy ? "Generating…" : "Generate"}
                </button>
              )}
            </ConfirmPopover>
          ) : canApprove(job, key) && key === "clear" && executor === "cloud" ? (
            /* Approving stage 1 on the cloud path QUEUES stage 2 for the worker — a paid press. */
            <ConfirmPopover message={approveQuestion()} confirmLabel="Approve and queue stage 2" onConfirm={() => void approve(key)}>
              {(toggle) => (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={busy || !previewLoaded}
                  onClick={toggle}
                  data-testid="case-approve"
                >
                  {busy ? "Approving…" : "Approve"}
                </button>
              )}
            </ConfirmPopover>
          ) : canApprove(job, key) ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !previewLoaded}
              onClick={() => void approve(key)}
              data-testid="case-approve"
            >
              {busy ? "Approving…" : "Approve"}
            </button>
          ) : undefined
        }
      >
        {mayRetry && (
          <ConfirmPopover message={retryQuestion(key)} confirmLabel="Retry" onConfirm={() => void retryStage(key)}>
            {(toggle) => (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={toggle}
                data-testid="case-retry"
              >
                Retry ({retriesLeft(job, key)} left)
              </button>
            )}
          </ConfirmPopover>
        )}
        {isHeld(job) && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => void checkAgain(key)}
            data-testid="case-check-again"
          >
            <Icon name="refresh" size={16} /> Check again
          </button>
        )}
        {ph === "failed" && (
          /* A failed job is TERMINAL on the server: `retry` answers 409. The way on is a new
             job from the photos, which spends nothing until the next paid press. */
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={startAgain}
            data-testid="case-start-again"
          >
            Start again from the photos
          </button>
        )}
      </ActionBar>
    </div>
  );
}
